import BigNumber from 'bignumber.js'
import { ethers } from 'ethers'
import {
  Address,
  createPublicClient,
  createWalletClient,
  custom,
  defineChain,
  erc20Abi,
  formatEther,
  formatUnits,
  isAddress,
  PublicClient,
  WalletClient,
  zeroAddress
} from 'viem'

import { EVMBridgeAbi } from '@/assets/bridgeAbi'
import { ENV_PARAMS } from '@/constants/evnParams'
import { FromChain, ToChain } from '@/hooks/services/useChainInfo'
import { TokenMeta } from '@/hooks/services/useTokenInfo'

import { CommonTools } from './CommonTools'

export default class EthereumTools implements CommonTools {
  chain: ReturnType<typeof defineChain>
  accountAddress?: Address
  isConnected: boolean = false
  bridgeAddress: Address = ENV_PARAMS.EVM_BRIDGE_ADDRESS
  publicClient: PublicClient
  walletClient: WalletClient

  constructor(chain: FromChain, onConnect: (address: string, isConnected: boolean) => void) {
    if (!window.tokenpocket.ethereum) throw new Error('No EVM wallet detected. Please install TokenPocket.')

    this.bridgeAddress = chain.bridgeAddress as Address
    this.chain = defineChain({
      id: chain.id,
      name: chain.name,
      nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
      rpcUrls: { default: { http: [chain.rpc] } }
    })

    this.publicClient = createPublicClient({
      chain: this.chain,
      transport: custom(window.tokenpocket.ethereum)
    }) as PublicClient
    this.walletClient = createWalletClient({
      chain: this.chain,
      transport: custom(window.tokenpocket.ethereum)
    })

    this.connect(onConnect)
  }

  async connect(onConnect: (address: string, isConnected: boolean) => void) {
    const currentChainId = await this.walletClient.getChainId()
    if (currentChainId !== this.chain.id) {
      await this.switchNework()
    }
    // await this.switchNework()
    const addresses = await this.walletClient.getAddresses()
    this.accountAddress = addresses[0]
    this.isConnected = true
    onConnect(this.accountAddress, this.isConnected)
  }

  async switchNework() {
    await this.walletClient.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${this.chain.id.toString(16)}` }]
    })
  }

  async bridge(parmas: {
    fromChain: FromChain
    toChain: ToChain
    fromToken: TokenMeta
    toToken: TokenMeta
    receviceAddress: string
    amount: number
    challengePeriod: number
    fee?: BigNumber
    nativeFee?: BigNumber
  }) {
    if (!this.accountAddress) return
    console.log('>>>>>> parmas: ', parmas)
    const {
      fromChain,
      toChain,
      fromToken,
      toToken,
      receviceAddress,
      amount,
      challengePeriod,
      nativeFee = new BigNumber(0),
      fee = new BigNumber(0)
    } = parmas
    const isNative = fromToken.address.toLowerCase() === zeroAddress
    const nativeFeeBN = ethers.utils.parseEther(nativeFee.toString())
    if (isNative) {
      const feeBN = ethers.utils.parseEther(fee.toString())
      const amount0 = ethers.utils.parseEther(amount.toString())
      const amount1 = amount0.sub(feeBN)

      return await this.walletClient.writeContractSync({
        account: this.accountAddress,
        chain: this.chain,
        abi: EVMBridgeAbi,
        address: this.bridgeAddress,
        functionName: 'receiveEther',
        args: [
          BigInt(fromChain.id),
          BigInt(toChain.id),
          toToken.address,
          isAddress(receviceAddress) ? receviceAddress : this.accountAddress,
          amount0.toBigInt(), // amount0
          amount1.toBigInt(), // amount1
          feeBN.toBigInt(), // tokenFee
          nativeFeeBN.toBigInt(), // ethFee
          BigInt(challengePeriod),
          ''
        ],
        value: amount0.add(nativeFeeBN).toBigInt()
      })
    } else {
      const allowance = await this.publicClient.readContract({
        abi: erc20Abi,
        address: fromToken.address as Address,
        functionName: 'allowance',
        args: [this.accountAddress, this.bridgeAddress]
      })

      const feeBN = ethers.utils.parseUnits(fee.toString(), fromToken.decimal)
      const amount0 = ethers.utils.parseUnits(amount.toString(), fromToken.decimal)
      const amount1 = amount0.sub(feeBN)

      if (allowance < amount0.toBigInt()) {
        await this.walletClient.writeContractSync({
          account: this.accountAddress,
          chain: this.chain,
          abi: erc20Abi,
          address: fromToken.address as Address,
          functionName: 'approve',
          args: [this.bridgeAddress, amount0.toBigInt()],
          timeout: 30_000
        })
      }

      return await this.walletClient.writeContractSync({
        account: this.accountAddress,
        chain: this.chain,
        abi: EVMBridgeAbi,
        address: this.bridgeAddress,
        functionName: 'receiveToken',
        args: [
          BigInt(fromChain.id),
          fromToken.address as Address,
          BigInt(toChain.id),
          toToken.address,
          isAddress(receviceAddress) ? receviceAddress : this.accountAddress,
          amount0.toBigInt(),
          amount1.toBigInt(),
          feeBN.toBigInt(),
          nativeFeeBN.toBigInt(),
          BigInt(challengePeriod),
          ''
        ],
        value: nativeFeeBN.toBigInt(),
        timeout: 30_000
      })
    }
  }

  async getBalance(tokenMeta: TokenMeta) {
    if (!this.accountAddress) return
    if (tokenMeta.address === zeroAddress) {
      const balance = await this.publicClient.getBalance({ address: this.accountAddress })
      return formatEther(balance)
    } else {
      const balance = await this.publicClient.readContract({
        abi: erc20Abi,
        address: tokenMeta.address as Address,
        functionName: 'balanceOf',
        args: [this.accountAddress]
      })
      return formatUnits(balance, tokenMeta.decimal)
    }
  }
}
